/**
 * Sidebar collapse, persisted across reloads.
 *
 * The class lives on `<html>` rather than in React state alone, because
 * index.html re-applies it from localStorage BEFORE first paint. Without that,
 * a collapsed sidebar would flash open on every page load while React boots.
 * This hook keeps the two in step.
 */

import { useCallback, useEffect, useState } from 'react';

export const NAV_COLLAPSED_STORAGE_KEY = 'wynxx.nav.collapsed';
const COLLAPSED_CLASS = 'nav-collapsed';
// Below this width the sidebar is always the icon rail: a 264px sidebar
// would leave a phone ~130px for the page. The person's own choice is kept
// and applies again on a wider screen.
const NARROW_QUERY = '(max-width: 720px)';

function useNarrowScreen(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

function readInitialState(): boolean {
  // The person's saved choice. Not the DOM class: on a narrow screen the
  // pre-paint script in index.html sets it regardless, and that must not be
  // mistaken for — and saved as — a preference.
  try {
    return localStorage.getItem(NAV_COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return typeof document !== 'undefined' && document.documentElement.classList.contains(COLLAPSED_CLASS);
  }
}

export function useNavCollapse() {
  const [collapsed, setCollapsed] = useState<boolean>(readInitialState);
  const narrow = useNarrowScreen();

  useEffect(() => {
    document.documentElement.classList.toggle(COLLAPSED_CLASS, collapsed || narrow);
  }, [collapsed, narrow]);

  useEffect(() => {
    try {
      localStorage.setItem(NAV_COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // Private browsing or storage disabled. The toggle still works for this
      // session; it simply will not be remembered.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((current) => !current), []);

  return { collapsed: collapsed || narrow, toggle, narrow };
}
